<template>
  <div class="sub-header">
    <div class="sub-header__info">
      <vita-search :options="options" v-if="options.length" v-model="option" />
    </div>
    <div
      class="sub-header__options"
      :class="{ 'has-hfrv': hasHolidaysFRVData }"
    >
      <div class="sub-header__counters">
        <vita-counter
          v-if="coreStaff"
          title="ФРВ"
          subtitle="Осн."
          frv-type="main"
          :info="coreStaff"
        />
        <vita-counter
          v-if="hasHolidaysFRVData"
          title="ФРВ РВ"
          frv-type="rv"
          :info="holidaysFRVData"
          :showIndicator="false"
        />
      </div>
      <vita-button
        v-if="isBeruRoute"
        class="rounded no-shadow"
        color="blue"
        width="inline"
        @click="openTariffsModal"
        data-qa="sub-header-button-tariffs"
        >Тарифы</vita-button
      >
      <div ref="burgerButton" class="sub-header__burger" @click="togglePopup"
           data-qa="sub-header-burger-toggle">
        <vita-icon name="burger-menu" />
      </div>
      <VitaPopup
        :isOpen="isPopupOpen"
        :targetRef="burgerButton"
        @close="closePopup"
      >
        <vita-counter
          type="secondary"
          frv-type="main"
          v-if="coreStaff"
          title="ФРВ"
          subtitle="Осн."
          :info="coreStaff"
        />
        <vita-counter
          type="secondary"
          frv-type="rv"
          v-if="holidaysCount > 0 && holidaysFRVData"
          title="ФРВ РВ"
          :info="holidaysFRVData"
        />
      </VitaPopup>
      <vita-info-modal
        :open="isTariffsModalOpen"
        @toggleInfoModal="closeTariffsModal"
        @open="onBeruInfoOpened"
        title="Тарифы"
        :modal-icon="false"
        modal-size="modal-secondary modal-beru-tariffs"
        close-on-click-outside
      >
        <template v-if="!isLoading && beruRates && beruRates.length === 0">
          <VitaEmptyPlaceholder
            qa-name="beru-tariffs-empty"
            icon="isEmpty"
            message="Нет доступных тарифов"
            icon-width="100"
            icon-height="100"
            :on-refresh-click="onBeruInfoOpened"
          />
        </template>
        <template v-else-if="!isLoading && hasError">
          <VitaEmptyPlaceholder
            qa-name="beru-tariffs-error"
            icon="isOffline"
            :message="loadErrorText"
            description="При загрузке данных произошла ошибка. Попробуйте обновить страницу позже."
            icon-width="100"
            icon-height="100"
            :on-refresh-click="onBeruInfoOpened"
          />
        </template>
        <div
          class="tariffs-info"
          v-else-if="isLoading || (beruRates && beruRates.length !== 0)"
        >
          <template v-if="isLoading">
            <div data-qa="beru-tariffs-shimmer" v-for="n in 2" :key="n" class="tariffs-info__month">
              <h4 class="tariffs-info__month-title">
                <Skeletor class="vue-skeletor-rounded-4" width="66" height="12" />
              </h4>
              <div class="tariffs-info__cards">
                <div
                  v-for="card in tariffCards"
                  :key="card.label"
                  class="tariffs-info__card"
                >
                  <div class="tariffs-info__card-value">
                    <Skeletor class="vue-skeletor-rounded-4" width="68" height="18" />
                  </div>
                  <div class="tariffs-info__card-label">{{ card.label }}</div>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div
              v-for="(rates, index) in beruRates"
              :key="rates.month"
              class="tariffs-info__month"
              :data-qa="`beru-tariffs-month-${index}-block`"
            >
              <h4
                class="tariffs-info__month-title"
                :data-qa="`beru-tariffs-month-${index}-title`"
              >{{ rates.month }}</h4>
              <div class="tariffs-info__cards">
                <div
                  v-for="card in tariffCards"
                  :key="card.label"
                  class="tariffs-info__card"
                  :data-qa="`beru-tariffs-month-${index}-card-${card.qaLabel}`"
                >
                  <div
                    class="tariffs-info__card-value"
                  >{{ rates[card.field] ?? "-" }}</div>
                  <div
                    class="tariffs-info__card-label"
                  >{{ card.label }}</div>
                </div>
              </div>
            </div>
          </template>
        </div>
        <div class="tariffs-info__hint" v-if="isLoading || (beruRates && beruRates.length > 0)" data-qa="beru-tariffs-hint">
          <vita-icon name="info" class="tariffs-info__hint-icon" />
          <p>Суммы выплат указаны с учетом НДФЛ</p>
        </div>
      </vita-info-modal>
    </div>
  </div>
</template>

<script lang="ts" setup>
import VitaButton from "@/components/UI/VitaButton.vue";
import VitaInfoModal from "@/components/UI/VitaInfoModal.vue";
import VitaCounter from "@/components/VitaCounter.vue";
import VitaPopup from "@/components/VitaPopup.vue";
import VitaSearch from "@/components/VitaSearch.vue";
import VitaEmptyPlaceholder from "@/components/UI/VitaEmptyPlaceholder.vue";
import { CounterPanel, ErrorResponseData, Option } from "@/types";
import { BeruRates } from "@/types/beru";
import { computed, onBeforeMount, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { AxiosError } from "axios";
import { reachGoal } from "@/utils/metrika";

const store = useStore();
const route = useRoute();

const isBeruRoute = computed(() => route.path === "/beru");

const options = computed<Option[]>(() => {
  return store.getters["data/pharmacies"];
});
const option = ref<Option | undefined>();
const coreStaff = computed<CounterPanel>(() => {
  return store.getters["data/coreStaff"];
});

const holidaysFRVData = computed<CounterPanel>(() => {
  return store.getters["data/FRV_RV"];
});

const holidaysCount = computed<number>(() => {
  return (
    store.getters["data/holidays"] && store.getters["data/holidays"].length
  );
});

const setOption = () => {
  if (
    !localStorage.getItem("pharmId") ||
    store.getters["data/pharmacies"].length === 0
  ) {
    return;
  }
  const pharmId =
    localStorage.getItem("pharmId") || store.getters["data/pharmacies"][0].id;
  const pharms = store.getters["data/pharmacies"];
  const isPharmId = pharms.some((item: Option) => item.id === +pharmId);
  if (isPharmId) {
    pharms.forEach((item: Option) => {
      if (item.id === +pharmId) {
        option.value = item;
      }
    });
  } else {
    option.value = store.getters["data/pharmacies"][0];
  }
};

onBeforeMount(() => {
  setOption();
});

const hasHolidaysFRVData = computed(
  () => holidaysCount.value > 0 && holidaysFRVData.value
);

const isPopupOpen = ref(false);
const burgerButton = ref<HTMLElement | null>(null);

const togglePopup = () => (isPopupOpen.value = !isPopupOpen.value);
const closePopup = () => (isPopupOpen.value = false);

const isTariffsModalOpen = ref(false);

const openTariffsModal = () => {
  isTariffsModalOpen.value = true;
  reachGoal('beru-tariffs-click');
};

const closeTariffsModal = () => {
  isTariffsModalOpen.value = false;
};

const beruRates = computed<BeruRates[] | null>(() => {
  return store.getters["beru/beruRates"];
});

const pharmId = computed<number>(() => {
  return store.getters['data/pharmId']
})

// Карточки для модалки с тарифами (скелетон и реальные данные) итерируются по этому массиву
const tariffCards: Array<{
  label: string;
  field: keyof BeruRates;
  qaLabel: string;
}> = [
  { label: "Фикс. сумма", field: "shiftCost", qaLabel: "fix-sum" },
  { label: "Фикс. сумма - 20%", field: "mainShiftCost", qaLabel: "fix-sum-main" },
  { label: "Среднее РД", field: "shiftCostRD", qaLabel: "average-rd" },
];

const isLoading = ref(false);
const hasError = ref(false);
const loadErrorText = ref('');

// Открытие модалки с тарифами Беру
const onBeruInfoOpened = async () => {
  isLoading.value = true;
  hasError.value = false;
  loadErrorText.value = ''
  try {
    await store.dispatch("beru/getBeruRates", pharmId.value);
  } catch (e) {
    const error = e as AxiosError<ErrorResponseData>
    hasError.value = true;
    // При статус код 500 стандартный текст ошибки, при остальных из "message" в ответе
    if (error.status !== 500 && error.response?.data) {
      loadErrorText.value = error.response?.data.message
    } else {
      loadErrorText.value = 'Ошибка загрузки данных'
    }
  } finally {
    isLoading.value = false;
  }
};
</script>

<style lang="scss">
@use "../static/style/variables.scss" as *;
@use "../static/style/mixins.scss" as *;

.sub-header {
  display: flex;

  &__info {
    width: $side-bar-width;
    min-width: $side-bar-width;
    padding: 10px 24px;
    border-right: 1px solid $border-color-default;
  }

  &__address {
    @include subtext-2;
  }

  &__options {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    padding: 12px 24px;
    max-width: 100%;
    overflow: hidden;
    min-height: 54px;
    flex: 1 0 auto;

    &.has-hfrv {
      .counter__item h4 {
        @media (max-width: 1420px) {
          max-width: 50px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .counter__proc {
        @media (max-width: 1140px) {
          display: none;
        }
      }
    }

    @media (max-width: 1059px) {
      justify-content: flex-end;
    }

    > div:not(:last-child) {
      @media (min-width: 1060px) {
        display: flex;
      }
    }
  }

  &__user {
    text-align: right;
    margin-right: 20px;

    p {
      @include font_3;
    }
  }

  &__burger {
    display: flex;
    align-items: center;
    cursor: pointer;

    @media (min-width: 1060px) {
      display: none;
    }
  }
}

.sub-header__counters {
  @media (max-width: 1059px) {
    display: none;
  }
}

.sub-header__controls {
  display: flex;
  align-items: center;

  @media (max-width: 1059px) {
    .report-controls {
      display: none;
    }
  }
}

.sub-header__print {
  @media (min-width: 961px) {
    display: flex;
    margin-left: 8px;
  }
  @media (max-width: 1059px) {
    display: none;
  }
}

.btn-elements-text:not(.secondary) {
  @media (max-width: 1550px) {
    display: none;
  }
}

.report-controls {
  position: relative;
}

.popup-content-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.tariffs-info {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__month {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__month-title {
    @include subtext-2;
    color: #999;
    font-weight: 400;
  }

  &__cards {
    display: flex;
    gap: 8px;
  }

  &__card {
    flex: 1;
    background: #eaf6ff;
    border-radius: 8px;
    padding: 16px 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    min-width: 111px;
  }

  &__card-value {
    font-weight: 600;
    font-size: 15px;
    line-height: 100%;
    letter-spacing: 0%;
    text-align: right;
    vertical-align: middle;
    color: #333;
  }

  &__card-label {
    font-weight: 400;
    font-size: 10px;
    line-height: 100%;
    letter-spacing: 0%;
    vertical-align: middle;
    color: #999;
  }

  &__hint {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 10px;

    p {
      font-weight: 500;
      font-size: 12px;
      line-height: 1.25;
      letter-spacing: 0;
      vertical-align: middle;
      color: #757575;
      margin-left: 4px;
    }
  }

  &__hint-icon {
    width: 24px;
    height: 24px;
  }
}
</style>
